import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Download, Lock, Eye, EyeOff, FileText, Info,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIE_OSCURABILI = [
  { id: 'psichiatria', label: 'Referti Psichiatrici', desc: 'Valutazioni, diagnosi e piani terapeutici di ambito psichiatrico', default: false },
  { id: 'hiv', label: 'Test Malattie Infettive / HIV', desc: 'Esiti test sierologici, terapie antiretrovirali e follow-up', default: false },
  { id: 'sociale', label: 'Relazioni Assistente Sociale', desc: 'Relazioni sociali, segnalazioni e valutazioni del contesto familiare', default: false },
  { id: 'ivg', label: 'Interruzioni Volontarie di Gravidanza', desc: 'Documentazione relativa a IVG (Legge 194/1978)', default: true },
  { id: 'dipendenze', label: 'Dipendenze Patologiche', desc: 'SerD, terapie sostitutive e percorsi di disintossicazione', default: false },
  { id: 'genetica', label: 'Referti di Genetica Medica', desc: 'Test genetici predittivi, consulenze e risultati', default: false },
];

export default function PatientPrivacy() {
  const [consensoFSE, setConsensoFSE] = useState(true);
  const [oscuramenti, setOscuramenti] = useState(() => {
    const init = {};
    CATEGORIE_OSCURABILI.forEach(c => { init[c.id] = c.default; });
    return init;
  });

  const oscuratiCount = Object.values(oscuramenti).filter(Boolean).length;

  const handleSave = () => {
    toast.success('Preferenze privacy aggiornate', {
      description: `Consenso FSE: ${consensoFSE ? 'Attivo' : 'Revocato'}. ${oscuratiCount} categori${oscuratiCount === 1 ? 'a' : 'e'} oscurat${oscuratiCount === 1 ? 'a' : 'e'}.`,
    });
  };

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-privacy">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-950/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-violet-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Privacy e Consensi</h1>
            <p className="text-sm text-muted-foreground">Gestisci il tuo Fascicolo Sanitario Elettronico e i consensi al trattamento dati</p>
          </div>
        </div>

        {/* Area 1: Informativa */}
        <Card className="border-0 shadow-[0_2px_12px_rgba(139,92,246,0.06)] rounded-3xl" data-testid="informativa-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 dark:bg-violet-950/20 flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-violet-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-bold text-foreground mb-2">Informativa sul Trattamento dei Dati Personali</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  La tua privacy è la nostra priorità. I tuoi dati sanitari sono trattati in conformità al <strong>Regolamento UE 2016/679 (GDPR)</strong> e al <strong>D.Lgs. 196/2003</strong> (Codice Privacy), come modificato dal D.Lgs. 101/2018. Il Titolare del trattamento è l'ASL Roma 2. I dati sono raccolti esclusivamente per finalità di cura, prevenzione e gestione del tuo percorso di Assistenza Domiciliare Integrata.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                  Hai diritto di accesso, rettifica, cancellazione e portabilità dei tuoi dati (artt. 15-22 GDPR). Per esercitare i tuoi diritti puoi rivolgerti al DPO dell'ASL Roma 2: <strong>dpo@aslroma2.it</strong>.
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border/20">
              <Button variant="outline" className="gap-2 rounded-xl text-xs font-semibold" data-testid="btn-scarica-informativa">
                <Download className="w-3.5 h-3.5" /> Scarica Informativa Completa (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Area 2: Consenso Globale FSE */}
        <Card className={`border-2 rounded-3xl transition-colors ${consensoFSE ? 'border-emerald-200 dark:border-emerald-800/40 shadow-[0_2px_12px_rgba(16,185,129,0.06)]' : 'border-red-200 dark:border-red-800/40 shadow-[0_2px_12px_rgba(239,68,68,0.06)]'}`} data-testid="consenso-fse-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${consensoFSE ? 'bg-emerald-50 dark:bg-emerald-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
                  {consensoFSE ? <Eye className="w-6 h-6 text-emerald-500" /> : <EyeOff className="w-6 h-6 text-red-500" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-base font-bold text-foreground">Consenso Fascicolo Sanitario Elettronico</h2>
                    <Badge className={`border-0 text-[10px] font-bold ${consensoFSE ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`} data-testid="fse-status-badge">
                      {consensoFSE ? 'Attivo' : 'Revocato'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Autorizza i professionisti sanitari coinvolti nel tuo percorso ADI ad alimentare e consultare il tuo Fascicolo Sanitario Elettronico (FSE 2.0), in conformità al DPCM 29 settembre 2015 n. 178.
                  </p>
                  {!consensoFSE && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/40">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700 dark:text-red-400 leading-relaxed">
                        <strong>Attenzione:</strong> Revocando il consenso, i professionisti sanitari non potranno consultare i tuoi documenti clinici pregressi. Questo potrebbe rallentare la tua presa in carico e compromettere la continuità assistenziale.
                      </p>
                    </motion.div>
                  )}
                </div>
              </div>
              <Switch
                checked={consensoFSE}
                onCheckedChange={setConsensoFSE}
                data-testid="toggle-consenso-fse"
              />
            </div>
          </CardContent>
        </Card>

        {/* Area 3: Oscuramento Documenti */}
        <Card className="border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-3xl" data-testid="oscuramento-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                <Lock className="w-6 h-6 text-slate-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold text-foreground">Oscuramento Documenti Sensibili</h2>
                  {oscuratiCount > 0 && (
                    <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-0 text-[10px] font-bold" data-testid="oscurati-count">
                      {oscuratiCount} oscurat{oscuratiCount === 1 ? 'a' : 'e'}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Ai sensi dell'art. 9 del GDPR e del D.Lgs. 196/2003, hai il diritto di oscurare specifiche categorie di documenti clinici. I documenti oscurati <strong>non saranno visibili</strong> all'équipe sanitaria coinvolta nel tuo percorso ADI.
                </p>
              </div>
            </div>

            <div className="space-y-1" data-testid="oscuramento-list">
              {CATEGORIE_OSCURABILI.map((cat, idx) => {
                const isOscurato = oscuramenti[cat.id];
                return (
                  <motion.div
                    key={cat.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className={`flex items-center justify-between px-4 py-3.5 rounded-2xl transition-colors ${isOscurato ? 'bg-red-50/60 dark:bg-red-950/10' : 'hover:bg-muted/20'}`}
                    data-testid={`oscuramento-${cat.id}`}
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-foreground">{cat.label}</p>
                        {isOscurato && (
                          <Badge className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-0 text-[9px] font-bold gap-1" data-testid={`badge-oscurato-${cat.id}`}>
                            <EyeOff className="w-2.5 h-2.5" /> Oscurato all'équipe
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{cat.desc}</p>
                    </div>
                    <Switch
                      checked={isOscurato}
                      onCheckedChange={(val) => setOscuramenti(prev => ({ ...prev, [cat.id]: val }))}
                      data-testid={`toggle-${cat.id}`}
                    />
                  </motion.div>
                );
              })}
            </div>

            {oscuratiCount > 0 && (
              <div className="mt-4 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                  <strong>Nota:</strong> L'oscuramento è reversibile in qualsiasi momento. In caso di emergenza medica, i professionisti sanitari potrebbero avere accesso ai dati oscurati ai sensi dell'art. 82 del D.Lgs. 196/2003.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer: Salva */}
        <div className="flex justify-end pb-6">
          <Button className="gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold px-6 h-11" onClick={handleSave} data-testid="btn-salva-privacy">
            <CheckCircle2 className="w-4 h-4" /> Salva Preferenze Privacy
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
